
// =============================================================================
// SCREEN SHARE MANAGEMENT HOOK (4 concurrent screens)
// =============================================================================

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { ScreenShare, Participant } from '@/types';
import { APP_CONFIG } from '@/utils/constants';

interface ScreenWithParticipant extends ScreenShare {
  participant?: Participant;
  slotIndex: number;
}

interface UseScreenShareOptions {
  activeScreenShares: ScreenShare[];
  participants: Participant[];
  currentParticipantId: string | null;
  maxScreens?: number;
}

interface UseScreenShareReturn {
  // Screen slots (always 4 slots, some may be empty)
  screenSlots: (ScreenWithParticipant | null)[];
  
  // Active screens
  activeScreens: ScreenWithParticipant[];
  
  // State
  availableSlots: number;
  isAtCapacity: boolean;
  currentUserScreenSlot: number | null;
  isCurrentUserSharing: boolean;
  
  // Helpers
  getParticipantForScreen: (participantId: string) => Participant | undefined;
  getScreenSlotIndex: (participantId: string) => number;
  canStartScreenShare: boolean;
  
  // Computed stats
  stats: {
    total: number;
    occupied: number;
    available: number;
  };
}

export function useScreenShare(options: UseScreenShareOptions): UseScreenShareReturn {
  const {
    activeScreenShares,
    participants,
    currentParticipantId,
    maxScreens = APP_CONFIG.MAX_CONCURRENT_SCREEN_SHARES,
  } = options;

  /**
   * Create screen slots with participant info
   */
  const screenSlots = useMemo<(ScreenWithParticipant | null)[]>(() => {
    const slots: (ScreenWithParticipant | null)[] = Array(maxScreens).fill(null);
    
    // Fill slots with active screen shares
    activeScreenShares?.forEach((share, index) => {
      if (index < maxScreens) {
        const participant = participants.find(p => p.id === share.participantId);
        slots[index] = {
          ...share,
          participant,
          slotIndex: index,
        };
      }
    });
    
    return slots;
  }, [activeScreenShares, participants, maxScreens]);

  /**
   * Get only occupied screen slots
   */
  const activeScreens = useMemo<ScreenWithParticipant[]>(() => {
    return screenSlots.filter((slot): slot is ScreenWithParticipant => slot !== null);
  }, [screenSlots]);

  /**
   * Calculate available slots
   */
  const availableSlots = useMemo(() => {
    return maxScreens - activeScreenShares?.length;
  }, [maxScreens, activeScreenShares?.length]);

  /**
   * Check if at capacity
   */
  const isAtCapacity = useMemo(() => {
    return activeScreenShares?.length >= maxScreens;
  }, [activeScreenShares?.length, maxScreens]);

  /**
   * Find current user's screen share slot
   */
  const currentUserScreenSlot = useMemo(() => {
    if (!currentParticipantId) return null;
    
    const slotIndex = screenSlots.findIndex(
      slot => slot?.participantId === currentParticipantId
    );
    
    return slotIndex >= 0 ? slotIndex : null;
  }, [screenSlots, currentParticipantId]);

  /**
   * Check if current user is sharing
   */
  const isCurrentUserSharing = useMemo(() => {
    return currentUserScreenSlot !== null;
  }, [currentUserScreenSlot]);

  /**
   * Check if current user can start screen share
   */
  const canStartScreenShare = useMemo(() => {
    return !isCurrentUserSharing && !isAtCapacity;
  }, [isCurrentUserSharing, isAtCapacity]);

  /**
   * Get participant info for a screen share
   */
  const getParticipantForScreen = useCallback((participantId: string): Participant | undefined => {
    return participants.find(p => p.id === participantId);
  }, [participants]);

  /**
   * Get slot index for a participant's screen share
   */
  const getScreenSlotIndex = useCallback((participantId: string): number => {
    return screenSlots.findIndex(slot => slot?.participantId === participantId);
  }, [screenSlots]);

  /**
   * Stats object
   */
  const stats = useMemo(() => ({
    total: maxScreens,
    occupied: activeScreenShares?.length,
    available: availableSlots,
  }), [maxScreens, activeScreenShares?.length, availableSlots]);

  return {
    // Screen slots
    screenSlots,
    activeScreens,
    
    // State
    availableSlots,
    isAtCapacity,
    currentUserScreenSlot,
    isCurrentUserSharing,
    
    // Helpers
    getParticipantForScreen,
    getScreenSlotIndex,
    canStartScreenShare,
    
    // Stats
    stats,
  };
}

/**
 * Hook for managing screen share grid layout
 */
interface UseScreenShareLayoutOptions {
  screenCount: number;
}

interface UseScreenShareLayoutReturn {
  gridCols: number;
  gridRows: number;
  containerClass: string;
}

export function useScreenShareLayout(options: UseScreenShareLayoutOptions): UseScreenShareLayoutReturn {
  const { screenCount } = options;

  const layout = useMemo(() => {
    switch (screenCount) {
      case 0:
        return { cols: 1, rows: 1 };
      case 1:
        return { cols: 1, rows: 1 };
      case 2:
        return { cols: 2, rows: 1 };
      case 3:
        return { cols: 2, rows: 2 }; // 3rd screen can span
      case 4:
        return { cols: 2, rows: 2 };
      default:
        return { cols: 2, rows: 2 };
    }
  }, [screenCount]);

  const containerClass = useMemo(() => {
    const baseClasses = 'grid gap-4 w-full h-full';
    return `${baseClasses} grid-cols-${layout.cols} grid-rows-${layout.rows}`;
  }, [layout]);

  return {
    gridCols: layout.cols,
    gridRows: layout.rows,
    containerClass,
  };
}

/**
 * Hook for screen share audio management
 * Since RealtimeKit/Dyte handles audio per participant, this is mostly for UI state
 */
interface UseScreenShareAudioOptions {
  activeScreens: ScreenWithParticipant[];
}

interface UseScreenShareAudioReturn {
  audioStates: Map<string, boolean>;
  toggleAudio: (participantId: string) => void;
  muteAll: () => void;
  unmuteAll: () => void;
}

export function useScreenShareAudio(options: UseScreenShareAudioOptions): UseScreenShareAudioReturn {
  const { activeScreens } = options;
  
  // Track muted state per participant (for UI only, actual muting handled by RealtimeKit)
  const [audioStates, setAudioStates] = useState<Map<string, boolean>>(new Map());

  // Initialize audio states for new screens and cleanup removed ones
  useEffect(() => {
    setAudioStates(prevStates => {
      const newStates = new Map(prevStates);
      
      // Add new screens
      activeScreens.forEach(screen => {
        if (!newStates.has(screen.participantId)) {
          newStates.set(screen.participantId, true); // Default: unmuted
        }
      });
      
      // Remove states for screens that are no longer active
      const activeParticipantIds = new Set(activeScreens.map(s => s.participantId));
      for (const [participantId] of newStates) {
        if (!activeParticipantIds.has(participantId)) {
          newStates.delete(participantId);
        }
      }
      
      // Only update if there are actual changes
      if (newStates.size !== prevStates.size) {
        return newStates;
      }
      
      // Check if any keys are different
      for (const [key] of newStates) {
        if (!prevStates.has(key)) {
          return newStates;
        }
      }
      
      return prevStates;
    });
  }, [activeScreens]); 
  const toggleAudio = useCallback((participantId: string) => {
    setAudioStates(prev => {
      const next = new Map(prev);
      const currentState = prev.get(participantId);
      next.set(participantId, !currentState);
      return next;
    });
  }, []);

  const muteAll = useCallback(() => {
    setAudioStates(prev => {
      const next = new Map(prev);
      for (const key of next.keys()) {
        next.set(key, false);
      }
      return next;
    });
  }, []);

  const unmuteAll = useCallback(() => {
    setAudioStates(prev => {
      const next = new Map(prev);
      for (const key of next.keys()) {
        next.set(key, true);
      }
      return next;
    });
  }, []);

  return {
    audioStates,
    toggleAudio,
    muteAll,
    unmuteAll,
  };
}